<template>
  <div class="custom-map-studio" ref="boxRef">
    <div id="container"></div>
    <div class="map-controls-hint">
      <span>滚轮缩放 · 拖拽移动画布 · 双击添加/删除标点</span>
      <button class="reset-view-btn" @click="resetView" title="重置地图视野">
        复位
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Konva from "konva";

const RED_ARROW_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 38" width="32" height="38">
  <defs>
    <filter id="shadow" x="-30%" y="-20%" width="160%" height="150%">
      <feDropShadow dx="0" dy="2.5" stdDeviation="2" flood-color="#000000" flood-opacity="0.3"/>
    </filter>
  </defs>
  <path d="M16 2 C8.268 2 2 8.268 2 16 C2 25.5 16 36 16 36 C16 36 30 25.5 30 16 C30 8.268 23.732 2 16 2 Z"
        fill="#ef4444"
        stroke="#ffffff"
        stroke-width="1.8"
        filter="url(#shadow)"/>
  <circle cx="16" cy="15" r="5" fill="#ffffff"/>
  <circle cx="16" cy="15" r="2.2" fill="#dc2626"/>
</svg>
`)}`;

export default defineComponent({
  name: "CustomeMap",
  data(): any {
    return {
      stage: null,
      mapGroup: null,
      baseMapNode: null,
      savedImages: [],
      currentMapUrl: "",
    };
  },
  mounted() {
    const boxEl = this.$refs.boxRef as HTMLElement;
    const stageWidth =
      boxEl && boxEl.clientWidth > 0 ? boxEl.clientWidth : 1000;
    const stageHeight =
      boxEl && boxEl.clientHeight > 0 ? boxEl.clientHeight : 500;

    this.stage = new Konva.Stage({
      container: "container",
      width: stageWidth,
      height: stageHeight,
      draggable: true,
    });

    const layer = new Konva.Layer();
    this.mapGroup = new Konva.Group();
    layer.add(this.mapGroup);
    this.stage.add(layer);

    // 滚轮缩放
    this.stage.on("wheel", (e: any) => {
      e.evt.preventDefault();
      const scaleBy = 1.08;
      const oldScale = this.stage.scaleX();
      const pointer = this.stage.getPointerPosition();
      if (!pointer) return;

      const mousePointTo = {
        x: (pointer.x - this.stage.x()) / oldScale,
        y: (pointer.y - this.stage.y()) / oldScale,
      };

      const direction = e.evt.deltaY > 0 ? -1 : 1;
      let newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
      newScale = Math.max(0.2, Math.min(5, newScale));

      this.stage.scale({ x: newScale, y: newScale });

      const newPos = {
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      };
      this.stage.position(newPos);
      this.stage.batchDraw();
    });

    window.addEventListener("resize", this.handleResize);

    // 双击打点 / 删除
    this.stage.on("dblclick dbltap", () => {
      const pos = this.stage.getPointerPosition();
      if (!pos || !this.mapGroup || !this.baseMapNode) return;

      const transform = this.stage.getAbsoluteTransform().copy().invert();
      const localPos = transform.point(pos);

      // 命中检测：是否点击了已有标记点
      const shape = this.stage.getIntersection(pos);
      if (
        shape &&
        shape.className === "Image" &&
        shape.name() &&
        shape.name().startsWith("marker-")
      ) {
        shape.destroy();
        this.stage.batchDraw();
        this.removeImageFromSaved(shape);
      } else {
        // 限制必须点在底图范围内
        const mapX = this.baseMapNode.x();
        const mapY = this.baseMapNode.y();
        const mapW = this.baseMapNode.width();
        const mapH = this.baseMapNode.height();

        if (
          localPos.x < mapX ||
          localPos.x > mapX + mapW ||
          localPos.y < mapY ||
          localPos.y > mapY + mapH
        ) {
          return; // 点击底图外留白区域不触发打点
        }

        const imageObj = new Image();
        imageObj.src = RED_ARROW_SVG;
        imageObj.onload = () => {
          const pinWidth = 28;
          const pinHeight = 34;

          const newShape = new Konva.Image({
            x: localPos.x - pinWidth / 2,
            y: localPos.y - pinHeight,
            image: imageObj,
            width: pinWidth,
            height: pinHeight,
            draggable: false,
            name: "marker-" + Date.now(),
          });

          // 计算基于底图的相对百分比比例（0 ~ 1）
          const relX = (localPos.x - mapX) / mapW;
          const relY = (localPos.y - mapY) / mapH;
          newShape.setAttr("relX", relX);
          newShape.setAttr("relY", relY);

          this.mapGroup.add(newShape);
          this.stage.batchDraw();

          this.saveImage(newShape);
        };
      }
    });

    this.$emit("ready");
  },
  methods: {
    handleResize() {
      const boxEl = this.$refs.boxRef as HTMLElement;
      if (boxEl && this.stage && boxEl.clientWidth > 0) {
        this.stage.width(boxEl.clientWidth);
        this.stage.height(boxEl.clientHeight || 500);
        this.stage.batchDraw();
      }
    },
    saveImage(image: Konva.Image) {
      this.savedImages.push({
        name: image.name(),
        relX: image.getAttr("relX"),
        relY: image.getAttr("relY"),
        // 兼容保留绝对坐标
        x: image.x(),
        y: image.y(),
        src: RED_ARROW_SVG,
      });
      localStorage.setItem("generatedImages", JSON.stringify(this.savedImages));
    },
    removeImageFromSaved(image: Konva.Image) {
      this.savedImages = this.savedImages.filter(
        (img: any) => img.name !== image.name()
      );
      localStorage.setItem("generatedImages", JSON.stringify(this.savedImages));
    },
    restoreImages() {
      if (!this.mapGroup || !this.baseMapNode) return;

      const mapX = this.baseMapNode.x();
      const mapY = this.baseMapNode.y();
      const mapW = this.baseMapNode.width();
      const mapH = this.baseMapNode.height();

      this.savedImages.forEach((img: any) => {
        const imageObj = new Image();
        imageObj.src = RED_ARROW_SVG;
        imageObj.onload = () => {
          const pinWidth = 28;
          const pinHeight = 34;

          let targetX = 0;
          let targetY = 0;

          // 优先通过底图相对比例还原，保证位置 100% 贴合地物
          if (typeof img.relX === "number" && typeof img.relY === "number") {
            targetX = mapX + img.relX * mapW - pinWidth / 2;
            targetY = mapY + img.relY * mapH - pinHeight;
          } else {
            // 兼容老数据（按原绝对坐标呈现）
            targetX = img.x;
            targetY = img.y;
          }

          const newShape = new Konva.Image({
            x: targetX,
            y: targetY,
            image: imageObj,
            width: pinWidth,
            height: pinHeight,
            draggable: false,
            name: img.name || "marker-" + Date.now(),
          });

          newShape.setAttr("relX", img.relX);
          newShape.setAttr("relY", img.relY);

          this.mapGroup.add(newShape);
          this.stage.batchDraw();
        };
      });
    },
    setMap(url: string) {
      if (!this.stage || !this.mapGroup || !url) return;
      this.currentMapUrl = url;

      const imageObj = new Image();
      imageObj.src = url;
      imageObj.onload = () => {
        if (this.baseMapNode) {
          this.baseMapNode.destroy();
          this.baseMapNode = null;
        }

        const stageWidth = this.stage.width();
        const stageHeight = this.stage.height();

        // 等比适配居中
        const scale = Math.min(
          stageWidth / imageObj.width,
          stageHeight / imageObj.height
        );
        const drawWidth = imageObj.width * scale;
        const drawHeight = imageObj.height * scale;

        const posX = (stageWidth - drawWidth) / 2;
        const posY = (stageHeight - drawHeight) / 2;

        this.baseMapNode = new Konva.Image({
          x: posX,
          y: posY,
          image: imageObj,
          width: drawWidth,
          height: drawHeight,
        });

        this.mapGroup.add(this.baseMapNode);
        this.baseMapNode.moveToBottom();

        this.resetView();

        const savedData = localStorage.getItem("generatedImages");
        if (savedData) {
          try {
            this.savedImages = JSON.parse(savedData);
            this.restoreImages();
          } catch (e) {
            console.error(e);
          }
        }
      };
    },
    resetView() {
      if (!this.stage) return;
      this.stage.position({ x: 0, y: 0 });
      this.stage.scale({ x: 1, y: 1 });
      this.stage.batchDraw();
    },
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    localStorage.setItem("generatedImages", JSON.stringify(this.savedImages));
    if (this.stage) {
      this.stage.destroy();
    }
  },
});
</script>

<style lang="less" scoped>
.custom-map-studio {
  width: 100%;
  height: 100%;
  min-height: 420px;
  position: relative;
  background-color: #f1f5f9;
  overflow: hidden;
  border-radius: 8px;
}

#container {
  width: 100%;
  height: 100%;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  :deep(.konvajs-content) {
    width: 100% !important;
    height: 100% !important;
  }
}

.map-controls-hint {
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(6px);
  color: #ffffff;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  pointer-events: none;
  z-index: 10;

  .reset-view-btn {
    pointer-events: auto;
    background: #5d5fef;
    border: none;
    color: #ffffff;
    padding: 3px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #4b4de0;
    }
  }
}
</style>
